import clsx from "clsx";
import { cva } from "cva";
import { ChevronDown } from "lucide-react";
import React from "react";
import {
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  Button,
  Focusable,
  ListBox,
  ListBoxItemProps,
  SelectValue,
  ValidationResult,
  composeRenderProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Description, FieldError, Label } from "./Field";
import { DropdownItem, DropdownSection, DropdownSectionProps } from "./ListBox";
import { Popover, PopoverProps } from "./Popover";
import { Tooltip } from "./Tooltip";
import { composeTailwindRenderProps, focusRing } from "./utils";

export type SelectProps<T extends object> = Omit<
  AriaSelectProps<T>,
  "children"
> & {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<T>;
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  buttonClassName?: string;
};

export function Select<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  buttonClassName,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect
      placeholder={props.placeholder ?? "Vælg"}
      {...props}
      validationBehavior="aria"
      isInvalid={!!errorMessage}
      className={composeTailwindRenderProps(
        props.className,
        "group flex flex-col",
      )}
    >
      {label && <Label className="mb-2">{label}</Label>}
      <Button
        className={composeTailwindRenderProps(
          focusRing,
          twMerge(
            "group border-theme-outline-variant disabled:border-theme-outline-variant/50 flex min-w-[200px] items-center justify-between gap-x-4 rounded-sm border p-3.5 font-bold xl:p-7",
            buttonClassName,
          ),
        )}
      >
        <SelectValue className="text-theme-on-background data-placeholder:text-theme-on-background-variant line-clamp-1 text-start text-sm" />
        <ChevronDown
          aria-hidden
          className={clsx(
            "text-theme-on-background size-5 cursor-pointer transition-transform duration-500 ease-in-out group-aria-expanded:[transform:scaleY(-1)]",
          )}
        />
      </Button>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <SelectPopover
        className="min-w-(--trigger-width)"
        placement="bottom"
        offset={8}
      >
        <ListBox items={items} className="max-h-[inherit] outline-hidden">
          {children}
        </ListBox>
      </SelectPopover>
    </AriaSelect>
  );
}

export function SelectItem({
  tooltip,
  ...props
}: ListBoxItemProps & { tooltip?: React.ReactNode }) {
  return (
    <DropdownItem
      {...props}
      className={(renderProps) =>
        twMerge(
          focusRing(renderProps),
          "hover:bg-theme-on-background-hover flex h-11 cursor-pointer items-center gap-x-3 rounded-md px-4 font-medium text-nowrap transition-[background-color] duration-200",
          renderProps.selectionMode === "single" &&
            renderProps.isSelected &&
            "text-theme-active",
          composeRenderProps(
            props.className,
            (className) => className,
          )(renderProps),
        )
      }
    >
      {(renderProps) => {
        const content = composeRenderProps(
          props.children,
          (children) => children,
        )(renderProps);

        if (tooltip) {
          return (
            <Tooltip tooltipText={tooltip}>
              <Focusable>
                <div
                  role="button" // According to ChatGPT, "button" is the most appropriate role for a focusable text element that triggers a tooltip
                  tabIndex={0}
                  className="truncate"
                >
                  {content}
                </div>
              </Focusable>
            </Tooltip>
          );
        }

        return <div className="truncate">{content}</div>;
      }}
    </DropdownItem>
  );
}

export function SelectSection<T extends object>(
  props: DropdownSectionProps<T>,
) {
  return <DropdownSection {...props} />;
}

interface SelectPopoverProps extends Omit<PopoverProps, "children"> {
  children: React.ReactNode;
}

const selectPopoverStyles = cva({
  base: "p-3 overflow-y-auto colors-glass backdrop-blur-glass rounded-md text-[11px]/[16px]",
  // variants: {
  //   isEntering: {
  //     true: "animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 placement-left:slide-in-from-right-1 placement-right:slide-in-from-left-1 ease-out duration-200",
  //   },
  //   isExiting: {
  //     true: "animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 placement-left:slide-out-to-right-1 placement-right:slide-out-to-left-1 ease-in duration-150",
  //   },
  // },
});

export function SelectPopover({
  children,
  className,
  ...props
}: SelectPopoverProps) {
  return (
    <Popover
      offset={0}
      {...props}
      className={composeRenderProps(className, (className, renderProps) =>
        selectPopoverStyles({ ...renderProps, className }),
      )}
    >
      {children}
    </Popover>
  );
}
